import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import type { SelectProps } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { deriveHeroFrom, deriveHeroTo } from 'app/theme';
import type { ReactElement } from 'react';
import {
  HeroDecorCircleBottom,
  HeroDecorCircleTop,
  HeroRoot as HeroSection,
} from 'shared/components/HeroContainer';

const FILTER_FIELD_BG = '#F8F6FD';
const FILTER_FIELD_BG_TRANSLUCENT = 'rgba(248, 246, 253, 0.6)';
const FILTER_FIELD_BORDER = 'rgba(34, 28, 90, 0.10)';
export const FILTER_FIELD_TEXT = '#5A5578';
const FILTER_PLACEHOLDER = '#8782A0';

/** Root layout for the Projects page — hero, filters, and table stacked with a fixed gap.
 * Uses minHeight (not height) so the page can grow taller than the viewport and let the
 * app shell's own scroll container handle overflow, instead of the table growing its own
 * internal scrollbar. */
export const PageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  gap: 20,
});

/** Outer wrapper for the entire filter area */
export const FilterWrapper = styled(Box)({
  // Spacing after this card comes from the page's own flex `gap`, not a margin
  // here — an explicit marginBottom would double up with that gap.
});

/** Single rounded card row containing all filter controls, matching the table/card design */
export const FilterGroup = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  padding: theme.spacing(1.5, 2),
}));

/** Wide free-text search field, icon + input in a tinted rounded box */
export const SearchFieldBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  flex: '1 1 220px',
  minWidth: 180,
  maxWidth: 320,
}));

export const SearchIconWrap = styled(Box)({
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  color: FILTER_PLACEHOLDER,
  pointerEvents: 'none',
});

export const SearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: 40,
  borderRadius: 10,
  border: `1px solid ${FILTER_FIELD_BORDER}`,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  paddingLeft: 36,
  paddingRight: 12,
  fontSize: 14,
  fontFamily: 'inherit',
  color: theme.palette.text.primary,
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&::placeholder': { color: FILTER_PLACEHOLDER },
  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}26`,
  },
}));

export const FilterSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  minWidth: 82,
  height: 40,
  fontSize: 12,
  fontWeight: 500,
  flexShrink: 0,
  borderRadius: 10,
  backgroundColor: active ? `${theme.palette.primary.main}0D` : FILTER_FIELD_BG_TRANSLUCENT,
  color: active ? theme.palette.primary.main : FILTER_FIELD_TEXT,
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 14,
    paddingRight: '26px !important',
    paddingTop: 0,
    paddingBottom: 0,
    height: '40px !important',
  },
  '& .MuiSelect-icon': {
    width: 12,
    height: 12,
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    opacity: 0.6,
    color: active ? theme.palette.primary.main : FILTER_FIELD_TEXT,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: active ? `${theme.palette.primary.main}4D` : FILTER_FIELD_BORDER,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: active ? `${theme.palette.primary.main}4D` : FILTER_FIELD_BORDER,
  },
})) as unknown as <T = unknown>(props: SelectProps<T> & { active?: boolean }) => ReactElement;

export const ApplyButton = styled(Button)(({ theme }) => ({
  height: 32,
  fontWeight: 600,
  fontSize: 12,
  borderRadius: 10,
  flexShrink: 0,
  paddingLeft: 12,
  paddingRight: 12,
  textTransform: 'none',
  background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 50%, ${deriveHeroTo(theme.palette.primary.main)} 100%)`,
  '&:hover': {
    background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${deriveHeroFrom(theme.palette.primary.main)} 50%, ${theme.palette.primary.main} 100%)`,
  },
  '&.Mui-disabled': {
    color: '#fff',
    background: theme.palette.action.disabledBackground,
  },
}));

/** Right-aligned "Clear filters" + Apply cluster, sits at the end of the filter row */
export const ClearFiltersRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: 'auto',
});

export const ClearFiltersLink = styled('button')(({ theme }) => ({
  flexShrink: 0,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  color: theme.palette.text.disabled,
  transition: 'color 0.15s ease',
  '&:hover': { color: theme.palette.text.primary },
}));

export const ActionCell = styled(Box)({
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  justifyContent: 'flex-end',
});

/** Row-actions menu item */
export const RowMenuItem = styled(MenuItem)({
  borderRadius: 8,
  fontSize: '0.875rem',
  gap: 8,
  minHeight: 0,
  paddingTop: 6,
  paddingBottom: 6,
  paddingLeft: 8,
  paddingRight: 8,
  color: '#141030',
  '&:hover, &:focus': {
    backgroundColor: FILTER_FIELD_BG,
  },
  '& svg': {
    flexShrink: 0,
  },
  '& .MuiListItemText-root': {
    margin: 0,
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    lineHeight: '20px',
  },
});

/** Destructive variant of RowMenuItem */
export const DeleteMenuItem = styled(RowMenuItem)(({ theme }) => ({
  color: theme.palette.error.main,
  '&:hover, &:focus': {
    backgroundColor: `${theme.palette.error.main}1A`,
  },
}));

/** Divider separating destructive actions in the row-actions menu */
export const RowMenuDivider = styled(Divider)({
  '&&': {
    marginTop: '4px',
    marginBottom: '4px',
  },
  marginLeft: '-4px',
  marginRight: '-4px',
  borderColor: 'rgba(34, 28, 90, 0.07)',
});

/** Outer table container styling for the Projects list — border/radius/shadow match the card shell */
export const projectsTableContainerSx = {
  borderColor: 'rgba(34, 28, 90, 0.07)',
  borderRadius: '14px',
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  height: 'auto',
  overflow: 'visible',
};

/** Paper/list overrides for the row-actions menu */
export const rowMenuSlotProps = {
  paper: {
    sx: {
      minWidth: 160,
      p: 0.5,
      borderRadius: '14px',
      border: '1px solid rgba(34, 28, 90, 0.07)',
      boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12)',
    },
  },
  list: { sx: { py: 0 } },
} as const;

/** Ghost icon button that triggers the row actions menu — always visible, not hover-revealed */
export const RowMenuButton = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  color: FILTER_FIELD_TEXT,
  '&:hover': {
    backgroundColor: FILTER_FIELD_BG,
    color: theme.palette.text.primary,
  },
}));

/** Colored dot for the project color indicator */
export const ColorDot = styled(Box)<{ dotcolor: string }>(({ dotcolor }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: dotcolor,
  flexShrink: 0,
  display: 'inline-block',
}));

export const NameCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 200,
});

/** Forces a column's rendered content to a fixed width. */
export const ColWidthBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'colwidth',
})<{ colwidth: number }>(({ colwidth }) => ({
  width: colwidth,
  minWidth: colwidth,
}));

export const ProjectNameText = styled('span')(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const TrackedText = styled('span')(({ theme }) => ({
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: theme.palette.text.primary,
}));

const STATUS_ACTIVE = '#6E4FF7';
const BILLABLE_COLOR = '#2D8C64';
const MUTED_CHIP_TEXT = '#8782A0';

/** Pill badge for the Access column (Public / Private) */
export const AccessChip = styled(Chip)({
  height: 20,
  borderRadius: 9999,
  fontSize: '0.6875rem',
  fontWeight: 600,
  backgroundColor: FILTER_FIELD_BG,
  color: FILTER_FIELD_TEXT,
});

/** Pill badge for the Billing column, tinted by billable state */
export const BillingChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'billable',
})<{ billable?: boolean }>(({ billable }) => ({
  height: 20,
  borderRadius: 9999,
  fontSize: '0.6875rem',
  fontWeight: 500,
  backgroundColor: billable ? `${BILLABLE_COLOR}1A` : FILTER_FIELD_BG,
  color: billable ? BILLABLE_COLOR : MUTED_CHIP_TEXT,
}));

/** Pill badge for the Status column, tinted by archived state */
export const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'archived',
})<{ archived?: boolean }>(({ archived }) => ({
  height: 20,
  borderRadius: 9999,
  fontSize: '0.6875rem',
  fontWeight: 600,
  backgroundColor: archived ? FILTER_FIELD_BG : `${STATUS_ACTIVE}1A`,
  color: archived ? MUTED_CHIP_TEXT : STATUS_ACTIVE,
}));

/** Colored swatch used in the color picker */
export const ColorSwatch = styled(Box)<{ swatchcolor: string; selected?: boolean }>(
  ({ theme, swatchcolor, selected }) => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: swatchcolor,
    cursor: 'pointer',
    flexShrink: 0,
    boxShadow: selected
      ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`
      : 'none',
    transform: selected ? 'scale(1.08)' : 'scale(1)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
    '&:hover': {
      transform: 'scale(1.08)',
    },
  }),
);

/** Row of color swatches inside the form */
export const ColorSwatchRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
});

/**
 * Text field used inside the create/edit-project dialog — tinted lavender background
 * with a purple hover/focus outline. Scoped to this dialog only (not a global
 * MuiTextField override) since that would re-tint every text field app-wide.
 *
 * Call sites pass `size="small"` for the 40px height — the app theme no longer sets a
 * global MuiTextField size default.
 */
export const FormTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main + '40',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
      borderWidth: 2,
      boxShadow: `0 0 0 4px ${theme.palette.secondary.main}1F`,
    },
  },
}));

/** Small label sitting above each create/edit-project form field */
export const FieldLabel = styled('label')(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(0.75),
  fontSize: '0.75rem',
  lineHeight: '1rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

/** Required-field asterisk */
export const RequiredMark = styled('span')({
  color: '#F43F5E',
});

/** Compact toggle switch used for the Public/Billable rows in the create/edit-project form */
export const FormSwitch = styled(Switch)(({ theme }) => ({
  width: 36,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 100%)`,
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    opacity: 1,
    backgroundColor: '#E2E8F0',
  },
}));

export const FormFieldStack = styled(Stack)({
  paddingTop: 0,
});

export const FormDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

export const FormDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

/** "Cancel" button in the create/edit-project dialog footer — dark text on a bordered white pill */
/** Paper overrides for the create/edit-project dialog */
export const formDialogPaperProps = {
  sx: {
    borderRadius: '18px',
    boxShadow: '0 24px 64px rgba(15, 23, 42, 0.16)',
  },
} as const;

/** Close (×) button pinned to the dialog's top-right corner */
export const DialogCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: 16,
  top: 16,
  width: 32,
  height: 32,
  borderRadius: 10,
  color: theme.palette.text.disabled,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

/** Dialog heading */
export const FormDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: 0,
  paddingRight: theme.spacing(5),
  fontSize: '1.125rem',
  lineHeight: '28px',
  fontWeight: 600,
}));

/** Vertical stack holding the Public / Billable toggle rows */
export const ToggleStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

/** Toggle row — resets MUI's negative label offset and sets a 12px gap */
export const ToggleRow = styled(FormControlLabel)(({ theme }) => ({
  marginLeft: 0,
  gap: theme.spacing(1.5),
}));

export const FormCancelButton = styled(Button)(({ theme }) => ({
  height: 40,
  padding: '5px 16px',
  borderRadius: 10,
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.text.secondary,
  },
}));

/** "Create Project"/"Save Changes" submit button — gradient fill */
export const FormSubmitButton = styled(Button)(({ theme }) => ({
  height: 40,
  borderRadius: 10,
  fontWeight: 600,
  background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${theme.palette.primary.main} 50%, ${deriveHeroTo(theme.palette.primary.main)} 100%)`,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    background: `linear-gradient(135deg, ${deriveHeroFrom(theme.palette.primary.main)} 0%, ${deriveHeroFrom(theme.palette.primary.main)} 50%, ${theme.palette.primary.main} 100%)`,
  },
  '&.Mui-disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

// Project Status Tab styles

export const TopRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
}));

export const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  minWidth: 400,
  flex: '0 0 auto',
}));

export const StatRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
}));

export const StatLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
  letterSpacing: 0.5,
  fontSize: '0.75rem',
}));

export const StatValuePrimary = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.error.dark,
}));

export const StatValue = styled(Typography)({
  fontWeight: 700,
});

export const StatValueLight = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const ChartCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  flex: 1,
  minWidth: 260,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 200,
}));

export const TableHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

export const ShowAllButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.secondary,
  fontSize: 13,
}));

// Project Settings Tab Styles

export const SettingsTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: 10,
    backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
    fontSize: '0.875rem',
  },
  '& .MuiSelect-icon': {
    width: 16,
    height: 16,
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: FILTER_PLACEHOLDER,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: FILTER_FIELD_BORDER,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: FILTER_FIELD_BORDER,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: FILTER_FIELD_BORDER,
    borderWidth: 1,
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
  },
}));

export const SettingsFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: '20px',
  color: theme.palette.text.primary,
  // Matches the design's mt-1.5 (Tailwind: 0.375rem = 6px) — not MUI's 1.5-unit spacing.
  marginBottom: theme.spacing(0.75),
}));

export const SettingsFieldDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.brandMuted,
}));

/** SettingsFieldDescription variant used below a field's control (input, button, swatch picker) */
export const SettingsFieldHelperText = styled(SettingsFieldDescription)(({ theme }) => ({
  marginTop: theme.spacing(0.75),
  marginBottom: 0,
}));

/** SettingsFieldLabel variant used as the heading of a radio-group section (no bottom margin) */
export const RadioSectionLabel = styled(SettingsFieldLabel)({
  marginBottom: 0,
});

/** SettingsFieldDescription variant used directly under a RadioSectionLabel */
export const RadioSectionDescription = styled(SettingsFieldDescription)(({ theme }) => ({
  marginTop: theme.spacing(0.25),
}));

export const RadioFormControl = styled(FormControl)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}));

export const RadioGroupRow = styled(RadioGroup)({
  gap: 20,
});

/** FormControlLabel used for a single radio option — flush margins, tight label gap */
export const RadioOptionLabel = styled(FormControlLabel)({
  marginLeft: 0,
  marginRight: 0,
  gap: 8,
});

/** Radio control sized to sit flush against RadioOptionLabel's text */
export const RadioOptionControl = styled(Radio)({
  padding: 0,
});

export const SettingsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

export const SettingsSection = styled(Box)({});

export const BillableRateInputRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const ColorPickerButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  borderColor: theme.palette.divider,
}));

export const ColorPreviewSwatch = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  borderRadius: 3,
  marginRight: theme.spacing(0.75),
}));

export const ColorPopoverContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
}));

// Project Access Tab styles

/** Wrapper for a label + description + radio-group settings block (Visibility, Billability) */
export const SettingsRadioSection = styled(Box)({});

export const AddMembersRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const AddMembersButton = styled(Button)({
  textTransform: 'none',
  fontWeight: 500,
});

// Member table cell styled components
export const MemberRowBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'removing',
})<{ removing?: boolean }>(({ removing }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  opacity: removing ? 0.5 : 1,
  transition: 'opacity 0.2s',
}));

export const MemberCellAvatar = styled(Avatar)({
  width: 32,
  height: 32,
  fontSize: '0.6875rem',
  fontWeight: 700,
  '&&': {
    backgroundColor: 'rgba(110, 79, 247, 0.1)',
    color: '#6E4FF7',
  },
});

export const MemberPrimaryText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'removing',
})<{ removing?: boolean }>(({ removing }) => ({
  textDecoration: removing ? 'line-through' : 'none',
}));

export const MemberSecondaryText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'removing',
})<{ removing?: boolean }>(({ theme, removing }) => ({
  color: theme.palette.text.secondary,
  textDecoration: removing ? 'line-through' : 'none',
}));

export const AccessDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const BillableRateCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export const BillableRateBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasValue',
})<{ hasValue: boolean }>(({ theme, hasValue }) => ({
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  minWidth: 64,
  color: hasValue ? theme.palette.text.primary : theme.palette.text.disabled,
  fontSize: 13,
  backgroundColor: theme.palette.background.default,
}));

export const ChangeRateButton = styled(Button)({
  fontSize: 13,
  textTransform: 'none',
  minWidth: 0,
  paddingLeft: 8,
  paddingRight: 8,
});

export const RemoveMenuItem = styled('li')(({ theme }) => ({
  color: theme.palette.error.main,
}));

// Project Detail Page styles

export const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 64,
});

export const ProjectDetailPageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const DetailBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export const BreadcrumbLink = styled(Link)({
  cursor: 'pointer',
  fontSize: 14,
});

export const ProjectTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

export const ProjectSubtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const DetailHero = styled(HeroSection)({
  paddingTop: 24,
  paddingBottom: 24,
});
export const DetailHeroDecorTop = HeroDecorCircleTop;
export const DetailHeroDecorBottom = HeroDecorCircleBottom;

export const DetailHeroContent = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

export const DetailHeroBreadcrumbs = styled(Breadcrumbs)({
  color: 'rgba(255,255,255,0.6)',
  fontSize: '0.8125rem',
  '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.6)' },
});

export const DetailHeroBreadcrumbLink = styled(Link)({
  cursor: 'pointer',
  color: 'rgba(255,255,255,0.6)',
  '&:hover': { color: '#fff' },
});

export const DetailHeroTitleRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
});

export const DetailHeroColorDot = styled(Box)<{ dotcolor: string }>(({ dotcolor }) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: dotcolor,
  flexShrink: 0,
  boxShadow: '0 0 0 2px rgba(255,255,255,0.2)',
}));

export const DetailHeroTitle = styled(Typography)({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#fff',
  letterSpacing: '-0.02em',
});

export const DetailHeroSubtitle = styled(Typography)({
  fontSize: '0.875rem',
  color: 'rgba(255,255,255,0.6)',
  marginTop: 2,
});

export const DetailHeroPill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'muted',
})<{ muted?: boolean }>(({ muted }) => ({
  borderRadius: 9999,
  padding: '2px 8px',
  fontSize: '0.625rem',
  fontWeight: 600,
  color: muted ? 'rgba(255,255,255,0.7)' : '#fff',
  backgroundColor: muted ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.15)',
}));

export const DetailHeroPillsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginLeft: 8,
});

export const DetailHeroStatsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 12,
  paddingTop: 8,
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

export const DetailHeroStatCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  borderRadius: 8,
  backgroundColor: 'rgba(255,255,255,0.14)',
  backdropFilter: 'blur(4px)',
  padding: '16px 20px',
});

export const DetailHeroStatLabel = styled('span')({
  fontSize: '0.6875rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'rgba(255,255,255,0.5)',
});

export const DetailHeroStatValue = styled('span')({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#fff',
  fontVariantNumeric: 'tabular-nums',
});

export const PillTabsWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  height: 40,
  width: 'fit-content',
  borderRadius: 8,
  backgroundColor: theme.palette.background.default,
  padding: 2,
  marginTop: 20,
  marginBottom: 20,
}));

export const PillTab = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  borderRadius: 10,
  padding: '6px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  }),
  backgroundColor: active ? theme.palette.background.paper : 'transparent',
  color: active ? theme.palette.text.primary : theme.palette.text.disabled,
  boxShadow: active ? '0 4px 12px rgba(34,28,90,0.10), 0 1px 3px rgba(0,0,0,0.06)' : 'none',
  '&:hover': {
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  },
}));

export const TabCardShell = styled(Paper)({
  border: '1px solid rgba(34, 28, 90, 0.07)',
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
  overflow: 'hidden',
});

export const MembersCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.75, 2.5),
  borderBottom: '1px solid rgba(34, 28, 90, 0.07)',
}));

export const MembersTitleGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const MembersTitleText = styled('span')({
  fontSize: '0.875rem',
  fontWeight: 600,
});

export const MemberCountBadge = styled('span')(({ theme }) => ({
  display: 'flex',
  height: 20,
  minWidth: 20,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 9999,
  padding: '0 6px',
  fontSize: '0.625rem',
  fontWeight: 700,
  backgroundColor: `${theme.palette.primary.main}1A`,
  color: theme.palette.primary.main,
}));

export const MemberSearchBox = styled(Box)({
  position: 'relative',
  marginLeft: 'auto',
  minWidth: 0,
  width: 224,
});

export const MemberSearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: 40,
  borderRadius: 10,
  border: `1px solid ${FILTER_FIELD_BORDER}`,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  paddingLeft: 32,
  paddingRight: 12,
  fontSize: 14,
  fontFamily: 'inherit',
  color: theme.palette.text.primary,
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&::placeholder': { color: theme.palette.text.disabled },
  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}26`,
  },
}));

export const MemberSearchIconWrap = styled(Box)({
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  pointerEvents: 'none',
  color: 'inherit',
  opacity: 0.6,
});

export const RoleFilterSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(() => ({
  height: 40,
  minWidth: 160,
  fontSize: '0.875rem',
  fontWeight: 500,
  borderRadius: 10,
  backgroundColor: FILTER_FIELD_BG_TRANSLUCENT,
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 0,
    paddingBottom: 0,
    height: '40px !important',
  },
  '& .MuiSelect-icon': {
    width: 16,
    height: 16,
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: FILTER_PLACEHOLDER,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: FILTER_FIELD_BORDER,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: FILTER_FIELD_BORDER,
  },
})) as unknown as <T = unknown>(props: SelectProps<T>) => ReactElement;

export const RolePill = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'roleKind',
})<{ roleKind: 'Owner' | 'Admin' | 'ProjectManager' | 'TeamMember' }>(({ roleKind }) => {
  const tints: Record<string, { bg: string; fg: string }> = {
    Owner: { bg: `${STATUS_ACTIVE}1A`, fg: STATUS_ACTIVE },
    Admin: { bg: `${STATUS_ACTIVE}1A`, fg: STATUS_ACTIVE },
    ProjectManager: { bg: '#F59E0B1A', fg: '#B45309' },
    TeamMember: { bg: FILTER_FIELD_BG, fg: MUTED_CHIP_TEXT },
  };
  const tint = tints[roleKind] ?? tints.TeamMember;
  return {
    height: 20,
    borderRadius: 9999,
    fontSize: '0.6875rem',
    fontWeight: 600,
    backgroundColor: tint.bg,
    color: tint.fg,
  };
});

export const MemberStatusBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  height: 20,
  borderRadius: 9999,
  fontSize: '0.6875rem',
  fontWeight: 600,
  backgroundColor: active ? `${BILLABLE_COLOR}1A` : FILTER_FIELD_BG,
  color: active ? BILLABLE_COLOR : MUTED_CHIP_TEXT,
}));
